import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  Image, 
  Video, 
  Save, 
  RefreshCw, 
  X,
  Eye,
  FileText,
  Settings,
  BookOpen,
  Trash2,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../config/api';
import { uploadMediaFile, deleteMediaFile, isImageFile, isVideoFile } from '../../utils/uploadHelper';

const StoriesManagement = () => {
  const [storiesMedia, setStoriesMedia] = useState({
    mediaType: 'image',
    mediaUrl: '',
    thumbnailUrl: '',
    title: '',
    description: '',
    altText: '',
    isActive: true,
    overlayOpacity: 0.2,
    storiesTitle: '',
    storiesDescription: '',
    storiesSubtitle: ''
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    fetchStoriesMedia();
  }, []);

  const fetchStoriesMedia = async () => {
    try {
      setLoading(true);
      const res = await api.get('/stories-media');
      if (res.ok) {
        const data = await res.json();
        
        // Ensure stories content fields have proper defaults
        const storiesMediaWithDefaults = {
          ...data,
          overlayOpacity: data.overlayOpacity !== undefined && !isNaN(data.overlayOpacity) ? data.overlayOpacity : 0.2
        };
        
        setStoriesMedia(storiesMediaWithDefaults);
        setPreviewUrl(data.mediaUrl || '');
        console.log('✅ Stories media loaded:', storiesMediaWithDefaults);
      } else {
        console.error('Failed to fetch stories media');
        toast.error('Failed to load stories media');
      }
    } catch (error) {
      console.error('Error fetching stories media:', error);
      toast.error('Error loading stories media');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Allow saving even without media file if user wants to edit content
    if (!storiesMedia.mediaUrl && !storiesMedia.storiesTitle && !storiesMedia.storiesDescription && !storiesMedia.storiesSubtitle) {
      toast.error('Please either upload a media file or enter some stories content to save');
      return;
    }

    try {
      setSaving(true);
      
      // Ensure media type is set before saving (only if media is being uploaded)
      if (storiesMedia.mediaUrl && !storiesMedia.mediaType) {
        toast.error('Please select a media type (image or video)');
        setSaving(false);
        return;
      }
      
      console.log('💾 Saving stories media with data:', storiesMedia);
      
      const response = await api.put('/stories-media', storiesMedia);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Save response:', result);
        toast.success('Stories media saved successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Backend error:', errorData);
        toast.error(errorData.error || 'Failed to save stories media');
      }
    } catch (error) {
      console.error('❌ Error saving stories media:', error);
      toast.error('Error saving stories media');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    
    // Automatically detect media type
    const isVideo = isVideoFile(file);
    const isImage = isImageFile(file);
    
    if (!isVideo && !isImage) {
      toast.error('Please upload a valid image or video file (e.g. MP4, MOV, WebM, JPG, PNG)');
      return;
    }

    const detectedType = isVideo ? 'video' : 'image';
    
    const toastId = toast.loading(`Uploading ${detectedType} (${(file.size / (1024 * 1024)).toFixed(1)} MB)...`);
    
    try {
      // Use uploadMediaFile helper (handles presigned S3 + fallback multipart)
      const result = await uploadMediaFile(file, 'stories', '/upload/story');
      const fileUrl = result.fileUrl || result.url;
      
      const updatedMedia = {
        ...storiesMedia,
        mediaType: detectedType,
        mediaUrl: fileUrl,
        thumbnailUrl: detectedType === 'video' ? storiesMedia.thumbnailUrl : ''
      };

      setStoriesMedia(updatedMedia);
      setPreviewUrl(fileUrl);
      setUploadedFiles([file]);

      // Automatically persist the new media URL
      try {
        await api.put('/stories-media', updatedMedia);
        console.log('💾 Auto-saved new stories media URL to database');
      } catch (saveErr) {
        console.warn('Auto-save after upload warning:', saveErr);
      }

      toast.dismiss(toastId);
      toast.success(`${detectedType.charAt(0).toUpperCase() + detectedType.slice(1)} uploaded and saved successfully!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.dismiss(toastId);
      toast.error(`Upload failed: ${error.message || 'Unknown error'}`);
    }
  };

  const handleThumbnailUpload = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    
    if (!isImageFile(file)) {
      toast.error('Please upload an image file for the video thumbnail');
      return;
    }
    
    const toastId = toast.loading('Uploading video thumbnail...');
    try {
      const result = await uploadMediaFile(file, 'stories', '/upload/single');
      const fileUrl = result.fileUrl || result.url;
      
      const updatedMedia = {
        ...storiesMedia,
        thumbnailUrl: fileUrl
      };

      setStoriesMedia(updatedMedia);

      // Auto-save
      try {
        await api.put('/stories-media', updatedMedia);
      } catch (e) {
        console.warn('Auto-save thumbnail warning:', e);
      }

      toast.dismiss(toastId);
      toast.success('Thumbnail uploaded and saved successfully!');
    } catch (error) {
      console.error('Thumbnail upload error:', error);
      toast.dismiss(toastId);
      toast.error(`Thumbnail upload failed: ${error.message}`);
    }
  };

  // Permanently delete video / media from store and database
  const handleDeleteMedia = async () => {
    if (!storiesMedia.mediaUrl && !previewUrl) {
      toast.error('No media to delete');
      return;
    }

    const mediaLabel = storiesMedia.mediaType === 'video' ? 'video' : 'image';
    const confirmed = window.confirm(
      `Are you sure you want to delete this ${mediaLabel} from storage and the Stories page? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setDeleting(true);
      const toastId = toast.loading(`Deleting ${mediaLabel} from storage and database...`);

      // 1. Delete from S3 storage
      if (storiesMedia.mediaUrl) {
        await deleteMediaFile(storiesMedia.mediaUrl);
      }
      if (storiesMedia.thumbnailUrl) {
        await deleteMediaFile(storiesMedia.thumbnailUrl);
      }

      // 2. Clear state
      const updated = {
        ...storiesMedia,
        mediaUrl: '',
        thumbnailUrl: ''
      };
      setStoriesMedia(updated);
      setPreviewUrl('');
      setUploadedFiles([]);

      // 3. Persist cleared state to backend
      const response = await api.put('/stories-media', updated);
      toast.dismiss(toastId);

      if (response.ok) {
        toast.success(`${mediaLabel.charAt(0).toUpperCase() + mediaLabel.slice(1)} deleted successfully from store!`);
      } else {
        toast.success(`${mediaLabel.charAt(0).toUpperCase() + mediaLabel.slice(1)} removed successfully!`);
      }
    } catch (error) {
      console.error('Delete media error:', error);
      toast.error(`Error deleting media: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Delete video thumbnail
  const handleDeleteThumbnail = async () => {
    if (!storiesMedia.thumbnailUrl) return;

    const confirmed = window.confirm('Are you sure you want to delete this video thumbnail?');
    if (!confirmed) return;

    try {
      const toastId = toast.loading('Deleting thumbnail...');
      await deleteMediaFile(storiesMedia.thumbnailUrl);

      const updated = {
        ...storiesMedia,
        thumbnailUrl: ''
      };
      setStoriesMedia(updated);

      await api.put('/stories-media', updated);
      toast.dismiss(toastId);
      toast.success('Thumbnail deleted successfully!');
    } catch (error) {
      console.error('Delete thumbnail error:', error);
      toast.error('Failed to delete thumbnail');
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'mediaType') {
      setStoriesMedia(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      setStoriesMedia(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const toggleActive = () => {
    setStoriesMedia(prev => ({
      ...prev,
      isActive: !prev.isActive
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-xl text-gray-600">Loading stories media...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-primary-600" />
            Stories Media Management
          </h1>
          <p className="text-gray-600 mt-2">
            Upload and manage the stories video or image and content displayed on the Community Stories page.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Form */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-primary-600" />
                Media Upload
              </h3>
              
              {/* Media Type Selection */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Media Type
                </label>
                <div className="flex space-x-4">
                  <label className={`flex items-center px-4 py-2.5 rounded-lg border-2 transition-all cursor-pointer ${
                    storiesMedia.mediaType === 'image' 
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-semibold' 
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="mediaType"
                      value="image"
                      checked={storiesMedia.mediaType === 'image'}
                      onChange={(e) => handleInputChange('mediaType', e.target.value)}
                      className="mr-2 text-primary-600 focus:ring-primary-500"
                    />
                    <Image className="w-4 h-4 mr-1.5" />
                    Image
                  </label>
                  <label className={`flex items-center px-4 py-2.5 rounded-lg border-2 transition-all cursor-pointer ${
                    storiesMedia.mediaType === 'video' 
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-semibold' 
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="mediaType"
                      value="video"
                      checked={storiesMedia.mediaType === 'video'}
                      onChange={(e) => handleInputChange('mediaType', e.target.value)}
                      className="mr-2 text-primary-600 focus:ring-primary-500"
                    />
                    <Video className="w-4 h-4 mr-1.5" />
                    Video
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {storiesMedia.mediaType === 'video' 
                    ? 'Supported video formats: MP4, MOV, WebM, AVI (up to 100MB)' 
                    : 'Supported image formats: PNG, JPG, JPEG, GIF, WebP (up to 50MB)'
                  }
                </p>
              </div>

              {/* File Upload Area */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload {storiesMedia.mediaType === 'video' ? 'Video' : 'Image'} File
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors bg-gray-50/50 hover:bg-gray-50">
                  <input
                    type="file"
                    accept={storiesMedia.mediaType === 'video' ? 'video/*,.mp4,.mov,.webm,.avi,.mkv,.m4v' : 'image/*,.jpg,.jpeg,.png,.webp,.gif'}
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer block">
                    {storiesMedia.mediaType === 'video' ? (
                      <Video className="w-10 h-10 text-primary-500 mx-auto mb-2" />
                    ) : (
                      <Upload className="w-10 h-10 text-primary-500 mx-auto mb-2" />
                    )}
                    <p className="text-sm font-medium text-gray-700">
                      Click to choose or drag & drop {storiesMedia.mediaType === 'video' ? 'a video' : 'an image'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {storiesMedia.mediaType === 'video' ? 'MP4, MOV, WebM up to 100MB' : 'PNG, JPG, GIF up to 50MB'}
                    </p>
                  </label>
                </div>
                
                {/* Active Media Status & Delete Button */}
                {storiesMedia.mediaUrl && (
                  <div className="mt-4 p-3.5 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 overflow-hidden">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div className="truncate">
                        <p className="text-sm font-semibold text-green-800 flex items-center gap-1.5">
                          <span>{storiesMedia.mediaType === 'video' ? '📹 Video' : '🖼️ Image'} is Active in Store</span>
                        </p>
                        <p className="text-xs text-green-700 truncate max-w-xs sm:max-w-sm">
                          {storiesMedia.mediaUrl}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleDeleteMedia}
                      disabled={deleting}
                      title={`Delete ${storiesMedia.mediaType} from store`}
                      className="flex-shrink-0 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs font-semibold flex items-center gap-1.5 shadow transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Delete {storiesMedia.mediaType === 'video' ? 'Video' : 'Media'}</span>
                    </button>
                  </div>
                )}

                {/* Video Thumbnail Upload */}
                {storiesMedia.mediaType === 'video' && (
                  <div className="mt-5 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Video Poster / Thumbnail (Optional)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-3.5 text-center hover:border-primary-400 transition-colors bg-white">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleThumbnailUpload(e.target.files)}
                        className="hidden"
                        id="thumbnail-upload"
                      />
                      <label htmlFor="thumbnail-upload" className="cursor-pointer block">
                        <Image className="w-6 h-6 text-gray-400 mx-auto mb-1.5" />
                        <p className="text-xs font-medium text-gray-600">
                          Click to upload video cover/poster image
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          PNG, JPG, or WebP up to 10MB
                        </p>
                      </label>
                    </div>
                    
                    {storiesMedia.thumbnailUrl && (
                      <div className="mt-3 flex items-center justify-between bg-blue-50 border border-blue-200 p-2.5 rounded-lg">
                        <span className="text-blue-800 text-xs font-medium truncate max-w-xs">
                          Thumbnail: {storiesMedia.thumbnailUrl}
                        </span>
                        <button
                          type="button"
                          onClick={handleDeleteThumbnail}
                          className="text-red-600 hover:text-red-800 text-xs font-semibold flex items-center gap-1 ml-2 flex-shrink-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete Thumbnail
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Title */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Media Caption / Title (Optional)
                </label>
                <input
                  type="text"
                  value={storiesMedia.title || ''}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                  placeholder="e.g. Empowering Community Through Beats"
                />
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Media Subtitle / Description (Optional)
                </label>
                <textarea
                  value={storiesMedia.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                  placeholder="Short description under the media"
                />
              </div>

              {/* Alt Text */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Alt Text (for accessibility)
                </label>
                <input
                  type="text"
                  value={storiesMedia.altText || ''}
                  onChange={(e) => handleInputChange('altText', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                  placeholder="Description for screen readers"
                />
              </div>

              {/* Stories Content Section */}
              <div className="border-t pt-5 mt-5">
                <h4 className="text-md font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary-600" />
                  Stories Page Text Content
                </h4>
                <p className="text-xs text-gray-500 mb-4">
                  Edit the header text displayed on the Community Stories page.
                </p>
                
                {/* Stories Title */}
                <div className="mb-3.5">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Stories Main Title
                  </label>
                  <input
                    type="text"
                    value={storiesMedia.storiesTitle || ''}
                    onChange={(e) => handleInputChange('storiesTitle', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder="Community Stories"
                  />
                </div>

                {/* Stories Description */}
                <div className="mb-3.5">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Stories Description
                  </label>
                  <textarea
                    value={storiesMedia.storiesDescription || ''}
                    onChange={(e) => handleInputChange('storiesDescription', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder="Discover inspiring journeys..."
                  />
                </div>

                {/* Stories Subtitle */}
                <div className="mb-3.5">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Stories Subtitle (Optional)
                  </label>
                  <textarea
                    value={storiesMedia.storiesSubtitle || ''}
                    onChange={(e) => handleInputChange('storiesSubtitle', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder="Subtitle or quote"
                  />
                </div>
              </div>

              {/* Active Status */}
              <div className="flex items-center justify-between border-t pt-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Stories Page Visibility
                  </label>
                  <p className="text-xs text-gray-500">
                    {storiesMedia.isActive ? 'Active and visible on Stories page' : 'Hidden from Stories page'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleActive}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    storiesMedia.isActive ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      storiesMedia.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Action Buttons: Save and Delete */}
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 btn-primary py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 font-semibold shadow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Stories Media
                    </>
                  )}
                </button>

                {storiesMedia.mediaUrl && (
                  <button
                    type="button"
                    onClick={handleDeleteMedia}
                    disabled={deleting}
                    className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center gap-2 font-semibold shadow transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete {storiesMedia.mediaType === 'video' ? 'Video' : 'Media'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Preview & Store Status */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-primary-600" />
                  Live Preview
                </h3>
                {previewUrl && (
                  <button
                    type="button"
                    onClick={handleDeleteMedia}
                    disabled={deleting}
                    className="text-xs font-semibold text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 border border-red-200 px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete {storiesMedia.mediaType === 'video' ? 'Video' : 'Media'}
                  </button>
                )}
              </div>
              
              {previewUrl ? (
                <div className="bg-gray-900 rounded-lg p-3 overflow-hidden shadow-inner">
                  {storiesMedia.mediaType === 'video' ? (
                    <video
                      key={previewUrl}
                      src={previewUrl}
                      poster={storiesMedia.thumbnailUrl || undefined}
                      controls
                      playsInline
                      className="w-full max-h-80 object-contain rounded-md mx-auto"
                    >
                      <source src={previewUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img
                      src={previewUrl}
                      alt={storiesMedia.altText || 'Stories preview'}
                      className="w-full max-h-80 object-contain rounded-md mx-auto"
                    />
                  )}
                  
                  <div className="mt-3 p-3 bg-white/10 backdrop-blur rounded text-white text-xs space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Format:</span>
                      <span className="font-semibold uppercase tracking-wider text-primary-300">
                        {storiesMedia.mediaType}
                      </span>
                    </div>
                    {storiesMedia.title && (
                      <div className="flex justify-between">
                        <span className="text-gray-300">Title:</span>
                        <span className="font-medium">{storiesMedia.title}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-300">Status:</span>
                      <span className={`font-semibold ${storiesMedia.isActive ? 'text-green-400' : 'text-red-400'}`}>
                        {storiesMedia.isActive ? '● Live on Site' : '○ Hidden'}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-10 text-center">
                  <FileText className="w-14 h-14 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-700 font-medium">
                    No {storiesMedia.mediaType} uploaded yet
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Upload a {storiesMedia.mediaType} using the form on the left to see live preview
                  </p>
                </div>
              )}
            </div>

            {/* Current Settings Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Settings className="w-4 h-4 text-blue-700" />
                Current Configuration
              </h4>
              <div className="text-xs text-blue-900 space-y-2">
                <p><strong>Active Media Type:</strong> {storiesMedia.mediaType}</p>
                <p className="break-all"><strong>Media URL:</strong> {storiesMedia.mediaUrl || '<none>'}</p>
                {storiesMedia.thumbnailUrl && (
                  <p className="break-all"><strong>Thumbnail URL:</strong> {storiesMedia.thumbnailUrl}</p>
                )}
                <p><strong>Visibility:</strong> {storiesMedia.isActive ? 'Active (Visible)' : 'Hidden'}</p>
                <p><strong>Stories Title:</strong> {storiesMedia.storiesTitle || 'Default'}</p>
                <p><strong>Stories Description:</strong> {storiesMedia.storiesDescription ? (storiesMedia.storiesDescription.length > 80 ? storiesMedia.storiesDescription.substring(0, 80) + '...' : storiesMedia.storiesDescription) : 'Default'}</p>
              </div>
            </div>

            {/* Stories Content Preview */}
            <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4 text-primary-600" />
                Stories Page Header Preview
              </h4>
              <div className="text-sm space-y-2 bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200">
                <h5 className="font-bold text-base text-gray-900">
                  {storiesMedia.storiesTitle || 'Community Stories'}
                </h5>
                <p className="text-xs text-gray-700 leading-relaxed">
                  {storiesMedia.storiesDescription || 'Discover the inspiring journeys of individuals whose lives have been touched by Beats of Washington.'}
                </p>
                {storiesMedia.storiesSubtitle && (
                  <p className="text-xs text-primary-600 italic">
                    {storiesMedia.storiesSubtitle}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoriesManagement;